import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./ui/accordion";

const FAQS = [
  {
    q: "Why should I choose LIFTILE over other manufacturers?",
    a: "Every LIFTILE machine is engineered using premium components, strict quality inspections and a safety-first approach to deliver reliable performance, long service life and dependable after-sales support.",
  },
  {
    q: "Can you customize the machine according to our project requirements?",
    a: "Yes. We customize lifting height, load capacity, bucket type, platform size, electrical configuration and other specifications to match your project requirements.",
  },
  {
    q: "Do you provide installation, commissioning and after-sales support?",
    a: "Yes. We provide installation guidance, commissioning support, spare parts availability and responsive after-sales service to ensure smooth machine operation.",
  },
  {
    q: "Which brands of components are used in your machines?",
    a: "Depending on the machine model and customer requirements, we use trusted brands such as Bonfiglioli, ABB, Schneider Electric, Usha Martin, FAG, Elecon, Polycab, Kirloskar and Crompton.",
  },
  {
    q: "What is your manufacturing and delivery timeline?",
    a: "Delivery depends on the machine model and customization requirements. Our team provides an estimated manufacturing and dispatch schedule during quotation confirmation.",
  },
  {
    q: "How can I get the right machine recommendation and quotation?",
    a: "Share your project details including required lifting height, load capacity and application. Our engineering team will recommend the most suitable machine and provide a detailed quotation.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

export default function FAQSection() {
  return (
    <section
      data-testid="faq-section"
      className="relative bg-liftile-cream py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      {/* Soft orange ambient accent */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none opacity-[0.18]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
          {/* ---------------- LEFT ---------------- */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="lg:sticky lg:top-28"
          >
            <div className="heading-eyebrow mb-4 text-liftile-orange">
              Knowledge Base
            </div>
            <h2
              data-testid="faq-heading"
              className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
            >
              Frequently Asked
              <br />
              <span className="text-liftile-orange">Questions</span>
            </h2>
            <p className="font-body text-liftile-matte-black/70 mt-6 text-base md:text-lg leading-relaxed max-w-md">
              Everything you need to know before choosing the right construction
              machinery for your project.
            </p>

            {/* Trust statement card */}
            <div className="mt-8 border-l-2 border-liftile-orange pl-5 max-w-md">
              <p className="font-body text-sm md:text-base text-liftile-matte-black/75 italic leading-relaxed">
                &ldquo;Still have questions? Our engineering team is ready to
                help you choose the right solution for your project.&rdquo;
              </p>
            </div>

            {/* Premium orange outline button */}
            <Link
              to="/contact"
              data-testid="faq-talk-to-expert"
              className="group inline-flex items-center gap-3 mt-9 border-2 border-liftile-orange text-liftile-orange font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-3.5 rounded-sm bg-transparent transition-all duration-400 hover:bg-liftile-orange hover:text-white hover:-translate-y-0.5 hover:shadow-[0_15px_35px_-10px_rgba(255,107,26,0.6)]"
            >
              Talk to an Expert
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* ---------------- RIGHT — Accordion ---------------- */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            transition={{ delay: 0.15 }}
          >
            <Accordion
              type="single"
              collapsible
              defaultValue="faq-0"
              className="space-y-3"
            >
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="group/item border-0 bg-white rounded-xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.12)] transition-all duration-400 data-[state=open]:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.28)] data-[state=open]:ring-1 data-[state=open]:ring-liftile-orange/60"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="group/trigger w-full hover:no-underline [&>svg]:hidden px-6 md:px-7 py-5 md:py-6 text-left"
                  >
                    <div className="flex items-start gap-5 w-full">
                      <span className="shrink-0 mt-1 font-heading text-xs uppercase tracking-[0.2em] text-liftile-orange font-semibold w-8 group-data-[state=open]/item:opacity-100 opacity-70 transition-opacity">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 font-heading text-base md:text-lg text-liftile-matte-black font-semibold leading-snug group-data-[state=open]/item:text-liftile-orange transition-colors duration-300">
                        {item.q}
                      </span>
                      <span
                        aria-hidden
                        className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-liftile-matte-black/10 text-liftile-matte-black/70 group-data-[state=open]/item:bg-liftile-orange group-data-[state=open]/item:text-white group-data-[state=open]/item:border-liftile-orange group-data-[state=open]/item:rotate-45 transition-all duration-400 ease-out"
                      >
                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-6 md:px-7 pb-6 md:pb-7 pt-0">
                    <div className="pl-13 ml-13" style={{ paddingLeft: "3.25rem" }}>
                      <div className="h-px bg-liftile-orange/30 mb-4" />
                      <p className="font-body text-sm md:text-base text-liftile-matte-black/75 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
